import { useState, useRef, useEffect } from "react";
import { X, Send, Compass, Package, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { API, api, money, resolveImg } from "../lib/api";
import { ProductCard } from "./ProductCard";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const KitCard = ({ kit }) => {
  const { addItem } = useCart();
  const [adding, setAdding] = useState(false);

  const addKit = async () => {
    setAdding(true);
    try {
      const { data: full } = await api.get(`/kits/${kit.slug}`);
      const configurable = (full.products || []).filter((p) => p.variations?.skus?.length > 1);
      const componentSkus = {};
      const labels = [];
      configurable.forEach((p) => {
        const sku = p.variations.skus.find((s) => s.inventory > 0) || p.variations.skus[0];
        componentSkus[p.slug] = sku.sku_code;
        labels.push(`${p.name.replace(/^Rothco /, "")}: ${Object.values(sku.specs || {}).join(" / ")}`);
      });
      addItem({
        item_type: "kit", slug: full.slug, name: full.name, price: full.bundle_price, image: full.image,
        component_skus: componentSkus, variant_label: labels.join(" · ") || null,
        contents: (full.products || []).map((p) => ({ slug: p.slug, name: p.name, image: p.images?.[0] || null })),
      });
    } catch {
      toast.error("Could not add kit — view it on the Ready Kits page");
    } finally { setAdding(false); }
  };

  return (
    <div data-testid={`advisor-kit-card-${kit.slug}`} className="group bg-white border border-black/10 flex flex-col transition-transform duration-150 hover:-translate-y-0.5">
      <Link to={`/kit/${kit.slug}`} className="relative block aspect-square overflow-hidden bg-[#F4F3F0]">
        <img src={resolveImg(kit.images?.[0])} alt={kit.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-[#4A5D23] text-white flex items-center gap-1"><Package size={11} /> Ready Kit</span>
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <Link to={`/kit/${kit.slug}`} className="font-heading font-bold tracking-tight text-sm leading-snug hover:text-[#4A5D23] transition-colors duration-150">{kit.name}</Link>
        <div className="flex items-center justify-between mt-auto pt-2">
          <p className="font-bold text-sm">{money(kit.price)}</p>
          <button data-testid={`advisor-kit-add-${kit.slug}`} onClick={addKit} disabled={adding}
            className="flex items-center gap-1 bg-[#121212] text-white px-3 py-2 text-[10px] font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150 disabled:opacity-50">
            <Plus size={12} /> {adding ? "Adding…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

const PROMPTS = [
  "I need a vehicle emergency kit.",
  "I'm going camping for three days.",
  "I want basic everyday-carry equipment.",
  "I want to improve my home's security.",
  "I need a tactical backpack.",
  "I want a preparedness kit for severe weather.",
];

const fmtAssistant = (text) => {
  const esc = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return DOMPurify.sanitize(esc
    .replace(/^#{1,3}\s+(.+)$/gm, "<strong>$1</strong>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^[-*]\s+/gm, "• "), { ALLOWED_TAGS: ["strong"] });
};

export const GearAdvisor = ({ open, onClose, initialPrompt }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [recommended, setRecommended] = useState([]);
  const sessionId = useRef(`advisor_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const bottomRef = useRef(null);
  const autoSent = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, recommended]);

  useEffect(() => {
    if (open && initialPrompt && autoSent.current !== initialPrompt) {
      autoSent.current = initialPrompt;
      send(initialPrompt);
    }
    if (!open) autoSent.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPrompt]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || streaming) return;
    setInput("");
    setRecommended([]);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/advisor/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId.current, message }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = "";
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const chunk = line.slice(6);
          if (chunk === "[DONE]") continue;
          if (chunk.startsWith("[ERROR]")) { full = chunk.slice(8); }
          else full += chunk.replaceAll("<|nl|>", "\n");
          const display = full.split(/\nPRODUCTS:/)[0];
          setMessages((m) => { const next = [...m]; next[next.length - 1] = { role: "assistant", content: display }; return next; });
        }
      }
      const match = full.match(/PRODUCTS:\s*(.+)/);
      if (match) {
        const { data } = await api.get(`/advisor/products?slugs=${encodeURIComponent(match[1].trim())}`);
        setRecommended(data);
      }
    } catch {
      setMessages((m) => { const next = [...m]; next[next.length - 1] = { role: "assistant", content: "The Gear Advisor is temporarily unavailable. Please try again." }; return next; });
    } finally {
      setStreaming(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div data-testid="gear-advisor-drawer" className="relative bg-white w-full max-w-lg h-full flex flex-col border-l border-black/10">
        <div className="bg-[#121212] text-white px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Compass size={22} className="text-[#8FA84E]" />
            <div>
              <p className="font-heading font-black uppercase tracking-tight text-sm">Shodan Gear Advisor</p>
              <p className="text-[10px] text-white/50 uppercase tracking-widest font-mono">AI-Powered Recommendations</p>
            </div>
          </div>
          <button data-testid="advisor-close-btn" onClick={onClose} aria-label="Close" className="hover:text-[#8FA84E] transition-colors duration-150"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#F4F3F0]">
          {messages.length === 0 && (
            <div>
              <p className="text-sm font-semibold mb-1">Tell us what you're preparing for, where you'll use your gear, and your budget.</p>
              <p className="text-xs text-black/50 mb-4">We'll help you find products that fit your needs.</p>
              <div className="space-y-2">
                {PROMPTS.map((p) => (
                  <button key={p} data-testid="advisor-prompt-btn" onClick={() => send(p)}
                    className="block w-full text-left text-xs border border-black/15 bg-white px-3 py-2.5 hover:border-[#4A5D23] hover:text-[#4A5D23] transition-colors duration-150">
                    "{p}"
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div data-testid={`advisor-msg-${m.role}`} className={`max-w-[85%] px-4 py-3 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[#121212] text-white" : "bg-white border border-black/10"}`}>
                {m.role === "assistant" && m.content ? <span dangerouslySetInnerHTML={{ __html: fmtAssistant(m.content) }} />
                  : m.content || (streaming && i === messages.length - 1 ? <span className="animate-pulse">Analyzing your needs…</span> : "")}
              </div>
            </div>
          ))}
          {recommended.length > 0 && (
            <div data-testid="advisor-recommendations">
              <p className="text-xs font-bold uppercase tracking-widest text-black/50 mb-2">Recommended Gear</p>
              <div className="grid grid-cols-2 gap-3">
                {recommended.map((p) => p.is_kit ? <KitCard key={p.slug} kit={p} /> : <ProductCard key={p.slug} product={p} eager />)}
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-black/10 p-4 flex gap-2 bg-white">
          <input data-testid="advisor-input" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Describe what you're preparing for…"
            className="flex-1 border border-black/20 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
          <button data-testid="advisor-send-btn" type="submit" disabled={streaming}
            className="bg-[#4A5D23] text-white px-4 disabled:opacity-50 hover:bg-[#3D4D1D] transition-colors duration-150" aria-label="Send">
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};
